import React, { useState, useMemo } from 'react';
import { Alert } from 'react-bootstrap';
import { ClipboardList, Check, TrendingDown } from 'lucide-react';
import { ComponentWithReplacement } from '../../types';
import { Building } from '../../../../../types/protocols';
import { getLifetimeForDirection } from '../../../TemplatesEditor/types';
import { calculateCurrentCondition } from '../../../../../Helpers/conditionUtils';
import { resolveCardinalDirection } from '../../utils/cardinalDirectionResolver';

interface ConditionModalProps {
    component: ComponentWithReplacement;
    /**
     * Massredigering: how many components get the assessment. The modal then
     * starts blank and shows no previous assessment, since they differ per component.
     */
    bulkCount?: number;
    building: Building | null;
    onSave: (conditionValue: number, comment: string) => Promise<void>;
    onClose: () => void;
}

/**
 * Modal for setting/updating component condition assessment
 * Displays slider with color gradient and years remaining calculation
 * Shows current degraded condition based on time elapsed since last assessment
 */
export const ConditionModal: React.FC<ConditionModalProps> = ({
    component,
    bulkCount,
    building,
    onSave,
    onClose
}) => {
    const isBulk = bulkCount !== undefined && bulkCount > 1;
    const assessedCondition = isBulk ? undefined : component.component.condition?.value?.value;
    const assessmentDate = isBulk ? undefined : component.component.condition?.updatedWhen;

    // Calculate current degraded condition based on time elapsed
    const currentDegradedCondition = useMemo(() => {
        if (assessedCondition === undefined || assessedCondition === null || !assessmentDate || !component.lifetime) {
            return null;
        }

        const cardinalDirection = resolveCardinalDirection(component.component, building);
        const directionalLifetime = getLifetimeForDirection(component.lifetime, cardinalDirection);
        const expectedLifetime = directionalLifetime.expectedLifetimeYears;

        if (expectedLifetime == null || expectedLifetime <= 0) {
            return null;
        }

        return calculateCurrentCondition(assessedCondition, assessmentDate, expectedLifetime);
    }, [assessedCondition, assessmentDate, component.lifetime, component.component, building]);

    // Initialize slider with current degraded condition (or assessed condition as fallback)
    const initialCondition = currentDegradedCondition ?? assessedCondition;
    const [conditionValue, setConditionValue] = useState<string>(initialCondition?.toFixed(1) || '');
    const [comment, setComment] = useState<string>(isBulk ? '' : component.component.condition?.value?.comment || '');
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const value = parseFloat(conditionValue);
        if (isNaN(value) || value < 0 || value > 10) {
            setError('Skickvärdet måste vara mellan 0 och 10');
            return;
        }

        setSaving(true);
        try {
            await onSave(value, comment);
            onClose();
        } catch (error) {
            console.error('Error saving condition:', error);
            setError('Ett fel uppstod vid sparande av skickbedömning. Försök igen.');
        } finally {
            setSaving(false);
        }
    };

    // Calculate years remaining for display
    const calculateYearsRemaining = (): number | null => {
        const value = parseFloat(conditionValue);
        // The years differ per component in bulk (direction, lifetime), so none are promised
        if (isNaN(value) || !component.lifetime || isBulk) {
            return null;
        }

        const cardinalDirection = resolveCardinalDirection(component.component, building);
        const directionalLifetime = getLifetimeForDirection(component.lifetime, cardinalDirection);
        const expectedLifetime = directionalLifetime.expectedLifetimeYears;
        if (expectedLifetime == null || expectedLifetime <= 0) {
            return null;
        }

        // The value is saved as a new assessment dated today, so no time has passed since it.
        // Floored like "år kvar" in the component header and the replacement row.
        return Math.max(0, Math.floor((value / 10) * expectedLifetime));
    };

    const yearsRemaining = calculateYearsRemaining();

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <ClipboardList size={20} className="me-2" />
                                Ange skick - {isBulk ? `${bulkCount} komponenter` : building && component.component.displayName(building)}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Stäng"
                                disabled={saving}
                            />
                        </div>
                        <div className="modal-body">
                            {/* Show previous assessment and calculated current condition */}
                            {assessedCondition !== undefined && assessedCondition !== null && assessmentDate && currentDegradedCondition !== null && (
                                <div className="alert alert-light mb-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                    <div className="d-flex align-items-start gap-2">
                                        <TrendingDown size={18} className="text-muted mt-1" />
                                        <div>
                                            <div className="mb-1">
                                                <strong>Senaste bedömning:</strong>{' '}
                                                {assessedCondition.toFixed(1)}/10
                                                <span className="text-muted ms-2">
                                                    ({new Date(assessmentDate).toLocaleDateString('sv-SE')})
                                                </span>
                                            </div>
                                            <div>
                                                <strong>Beräknat nuvarande skick:</strong>{' '}
                                                <span className={currentDegradedCondition < assessedCondition ? 'text-warning' : ''}>
                                                    {currentDegradedCondition.toFixed(1)}/10
                                                </span>
                                                {currentDegradedCondition < assessedCondition && (
                                                    <span className="text-muted ms-2">
                                                        (−{(assessedCondition - currentDegradedCondition).toFixed(1)} sedan bedömning)
                                                    </span>
                                                )}
                                            </div>
                                            <small className="text-muted d-block mt-1">
                                                Skicket degraderas baserat på förväntad livslängd
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isBulk && (
                                <p className="text-muted small">
                                    Samma skick och kommentar sparas som en ny bedömning på alla {bulkCount} komponenterna.
                                </p>
                            )}

                            {error && (
                                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="conditionValue" className="form-label d-flex justify-content-between align-items-center">
                                        <span>Nytt skick <span className="text-danger">*</span></span>
                                        <span className="badge bg-primary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                                            {conditionValue || '0'}
                                        </span>
                                    </label>
                                    {yearsRemaining !== null && (
                                        <div style={{ fontSize: '0.875rem', color: '#6c757d', marginTop: '0.25rem' }}>
                                            {yearsRemaining < 1 ? 'Mindre än 1 år kvar' : `Ca ${yearsRemaining} år kvar`}
                                        </div>
                                    )}
                                    <div style={{ position: 'relative', padding: '20px 0' }}>
                                        <div style={{
                                            position: 'absolute',
                                            width: '100%',
                                            height: '12px',
                                            borderRadius: '6px',
                                            background: 'linear-gradient(to right, #a83b60 0%, #fee5ad 50%, #67ecaa 100%)',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 0
                                        }} />
                                        <input
                                            type="range"
                                            className="form-range"
                                            id="conditionValue"
                                            min="0"
                                            max="10"
                                            step="0.1"
                                            value={conditionValue || '0'}
                                            onChange={(e) => setConditionValue(e.target.value)}
                                            required
                                            disabled={saving}
                                            style={{
                                                position: 'relative',
                                                zIndex: 1,
                                                background: 'transparent',
                                                height: '12px'
                                            }}
                                        />
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <small className="form-text text-muted">0 = behöver ersättas</small>
                                        <small className="form-text text-muted">10 = mycket bra skick</small>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="conditionComment" className="form-label">
                                        Kommentar (valfritt)
                                    </label>
                                    <textarea
                                        className="form-control"
                                        id="conditionComment"
                                        rows={3}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        disabled={saving}
                                        placeholder="Lägg till en kommentar om skicket..."
                                    />
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={onClose}
                                        disabled={saving}
                                    >
                                        Avbryt
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || !conditionValue}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Sparar...
                                            </>
                                        ) : (
                                            <>
                                                <Check size={16} className="me-2" />
                                                Spara skick
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
